import { useState, useMemo } from "react";
import useSWR from 'swr';
import TabPageLayout, { DataCard } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import {
  Building2,
  Calendar,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  ShoppingCart,
  TrendingUp,
  CheckCircle
} from "lucide-react";
import { BACKEND_URL } from '../config/api';
import DataTable from "./DataTable";
import StatusBadge from "./StatusBadge";
import { logAdminEvent } from '../utils/superAdmin';
import axios from "axios";
import { toast } from "sonner";
import jsPDF from 'jspdf';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";

const PLATFORM_API = `${BACKEND_URL}/api/v2/super-admin`;

const fetcher = async (url) => {
  const token = localStorage.getItem("jwtToken");
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (response.data.organizations) {
    return response.data.organizations;
  }
  return response.data;
};

const formatCurrency = (amount, currency = 'USD') => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
};

// Plain white stat card — concise, professional
const StatCard = ({ label, value, subtext, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col justify-between gap-4 min-h-[120px]">
    <div className="flex items-start justify-between">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      {Icon && (
        <div className="p-2 rounded-lg bg-gray-50">
          <Icon className="w-4 h-4 text-gray-500" />
        </div>
      )}
    </div>
    <div>
      <p className="text-2xl sm:text-3xl font-semibold text-gray-900">{value}</p>
      {subtext && <p className="text-xs mt-1 text-gray-500">{subtext}</p>}
    </div>
  </div>
);

const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
        <p className="font-medium text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const SuperAdminDashboard = () => {
  // Date range — default to last 6 months
  const [startDate, setStartDate] = useState(() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return sixMonthsAgo.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const applyPreset = (preset) => {
    const now = new Date();
    let start, end = now.toISOString().split('T')[0];

    switch (preset) {
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        break;
      case '3months':
        start = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
        break;
      case '6months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString().split('T')[0];
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      case 'all':
        start = '2020-01-01';
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
    setDatePickerOpen(false);
  };

  const { data: orgsData, error: orgsError } = useSWR(
    `${PLATFORM_API}/organizations`,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 }
  );

  const { data: financials } = useSWR(
    `${PLATFORM_API}/platform-financials?start_date=${startDate}&end_date=${endDate}`,
    fetcher,
    { revalidateOnFocus: true }
  );

  const stats = useMemo(() => {
    const orgs = orgsData || [];
    const active = orgs.filter(o => o.status === 'active' || o.status === 'approved').length;
    const pending = orgs.filter(o => o.status === 'pending').length;
    const suspended = orgs.filter(o => o.status === 'suspended').length;
    const recentSignups = [...orgs]
      .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
      .slice(0, 5);

    return {
      totalOrgs: orgs.length,
      activeOrgs: active,
      pendingOrgs: pending,
      suspendedOrgs: suspended,
      recentSignups,
    };
  }, [orgsData]);

  const isLoading = !orgsData && !orgsError;

  const summary = financials?.summary;
  const topOrgs = financials?.top_organizations || [];
  const monthlyChart = financials?.monthly_chart || [];
  const currency = summary?.currency || 'USD';
  const multiCurrency = (summary?.by_currency?.length || 0) > 1;

  const pieData = useMemo(() => {
    if (!summary) return [];
    const operatorRevenue = (summary.gross_gmv || 0) - (summary.platform_fees || 0);
    return [
      { name: 'Operators', value: operatorRevenue, color: '#10B981' },
      { name: 'Platform', value: summary.platform_fees || 0, color: '#2563EB' }
    ];
  }, [summary]);

  // ---------- Exports ----------
  const exportJSON = () => {
    const data = {
      reportInfo: {
        generatedAt: new Date().toISOString(),
        dateRange: { start: startDate, end: endDate },
        generatedBy: 'BookingFlows Admin'
      },
      financialSummary: {
        grossGMV: summary?.gross_gmv || 0,
        totalRefunds: summary?.refunds || 0,
        netGMV: summary?.net_gmv || 0,
        platformRevenue: summary?.platform_fees || 0,
        totalBookings: summary?.booking_count || 0,
        refundCount: summary?.refund_count || 0,
        avgBookingValue: summary?.avg_booking_value || 0,
        activeCompanies30d: summary?.active_companies_30d || 0,
        currency,
        byCurrency: summary?.by_currency || []
      },
      organizationStats: {
        total: stats.totalOrgs,
        active: stats.activeOrgs,
        pending: stats.pendingOrgs,
        suspended: stats.suspendedOrgs
      },
      organizationBreakdown: topOrgs.map(org => ({
        name: org.name,
        slug: org.slug,
        gmv: org.gmv,
        platformFee: org.fees,
        bookings: org.bookings,
        currency: org.currency || 'USD'
      })),
      monthlyBreakdown: monthlyChart
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookingflows-report-${startDate}-to-${endDate}.json`;
    a.click();
    URL.revokeObjectURL(url);
    logAdminEvent('report_exported', { report: 'platform_overview', format: 'json', start: startDate, end: endDate });
    toast.success("Report exported as JSON");
  };

  const exportCSV = () => {
    const rows = [
      ['BOOKINGFLOWS FINANCIAL REPORT'],
      [`Date Range: ${startDate} to ${endDate}`],
      [`Generated: ${new Date().toISOString()}`],
      [''],
      ['FINANCIAL SUMMARY'],
      ['Metric', 'Value'],
      ['Gross GMV', (summary?.gross_gmv || 0).toFixed(2)],
      ['Refunded GMV', (summary?.refunds || 0).toFixed(2)],
      ['Net GMV', (summary?.net_gmv || 0).toFixed(2)],
      ['Platform Revenue', (summary?.platform_fees || 0).toFixed(2)],
      ['Total Bookings', summary?.booking_count || 0],
      ['Active Companies (30d)', summary?.active_companies_30d || 0],
      ['Avg Booking Value', (summary?.avg_booking_value || 0).toFixed(2)],
      [''],
      ['ORGANIZATION BREAKDOWN'],
      ['Name', 'Slug', 'Currency', 'GMV', 'Platform Fee', 'Bookings', 'Avg Booking'],
    ];

    topOrgs.forEach(org => {
      const avgBooking = org.bookings > 0 ? (org.gmv / org.bookings).toFixed(2) : 0;
      rows.push([`"${org.name}"`, org.slug, org.currency || 'USD', org.gmv?.toFixed(2), org.fees?.toFixed(2), org.bookings, avgBooking]);
    });

    if (monthlyChart.length) {
      rows.push([''], ['MONTHLY BREAKDOWN'], ['Month', 'GMV', 'Platform Fee', 'Bookings']);
      monthlyChart.forEach(m => {
        rows.push([m.month, m.gmv?.toFixed(2), m.fees?.toFixed(2), m.bookings]);
      });
    }

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookingflows-report-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    logAdminEvent('report_exported', { report: 'platform_overview', format: 'csv', start: startDate, end: endDate });
    toast.success("Report exported as CSV");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('BookingFlows Financial Report', pageWidth / 2, y, { align: 'center' });
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`${startDate} to ${endDate}  •  Generated ${new Date().toLocaleDateString()}`, pageWidth / 2, y, { align: 'center' });
    y += 14;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Financial Summary', 20, y);
    y += 9;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const summaryData = [
      ['Gross GMV', formatCurrency(summary?.gross_gmv, currency)],
      ['Platform Revenue', formatCurrency(summary?.platform_fees, currency)],
      ['Total Bookings', (summary?.booking_count || 0).toString()],
      ['Refunded GMV', formatCurrency(summary?.refunds, currency)],
      ['Net GMV', formatCurrency(summary?.net_gmv, currency)],
      ['Active Companies (30d)', (summary?.active_companies_30d || 0).toString()],
    ];

    summaryData.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label + ':', 20, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 80, y);
      y += 7;
    });

    y += 8;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Organization Breakdown', 20, y);
    y += 9;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Organization', 20, y);
    doc.text('GMV', 90, y);
    doc.text('Platform Fee', 120, y);
    doc.text('Bookings', 160, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    topOrgs.slice(0, 15).forEach(org => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(org.name?.substring(0, 30) || '', 20, y);
      doc.text(formatCurrency(org.gmv, org.currency), 90, y);
      doc.text(formatCurrency(org.fees, org.currency), 120, y);
      doc.text((org.bookings || 0).toString(), 160, y);
      y += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
      doc.text('BookingFlows - Confidential', 20, 290);
    }

    doc.save(`bookingflows-report-${startDate}-to-${endDate}.pdf`);
    logAdminEvent('report_exported', { report: 'platform_overview', format: 'pdf', start: startDate, end: endDate });
    toast.success("PDF report exported");
  };

  // ---------- Helpers ----------
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <TabPageLayout
      title="Overview"
      subtitle={`${formatDateShort(startDate)} - ${formatDateShort(endDate)}`}
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Range Picker */}
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 gap-2" data-testid="overview-date-range-button">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">{formatDateShort(startDate)} - {formatDateShort(endDate)}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end">
              <div className="space-y-4">
                <div className="text-sm font-medium text-gray-900">Select Date Range</div>

                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'week', label: '7 Days' },
                    { key: 'month', label: 'This Month' },
                    { key: '3months', label: '3 Months' },
                    { key: '6months', label: '6 Months' },
                    { key: 'year', label: 'This Year' },
                    { key: 'all', label: 'All Time' },
                  ].map(preset => (
                    <button
                      key={preset.key}
                      data-testid={`date-preset-${preset.key}`}
                      onClick={() => applyPreset(preset.key)}
                      className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-9"
                      data-testid="date-range-start-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">End Date</label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-9"
                      data-testid="date-range-end-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={() => setDatePickerOpen(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9"
                  data-testid="date-range-apply-button"
                >
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" data-testid="overview-export-button">
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportPDF} data-testid="export-pdf-option">
                <FileText className="w-4 h-4 mr-2" />PDF Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportCSV} data-testid="export-csv-option">
                <FileSpreadsheet className="w-4 h-4 mr-2" />CSV Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportJSON} data-testid="export-json-option">
                <FileJson className="w-4 h-4 mr-2" />JSON Data
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Key Metrics — clean white cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Gross GMV"
            value={multiCurrency ? (
              <span className="block space-y-0.5">
                {summary.by_currency.map((c) => (
                  <span key={c.currency} className="block text-xl sm:text-2xl font-semibold text-gray-900">
                    {formatCurrency(c.gmv, c.currency)}
                  </span>
                ))}
              </span>
            ) : formatCurrency(summary?.gross_gmv, currency)}
            subtext={`${(summary?.booking_count || 0).toLocaleString()} bookings`}
            icon={ShoppingCart}
            testId="stat-gross-gmv"
          />
          <StatCard
            label="Platform Revenue"
            value={multiCurrency ? (
              <span className="block space-y-0.5">
                {summary.by_currency.map((c) => (
                  <span key={c.currency} className="block text-xl sm:text-2xl font-semibold text-gray-900">
                    {formatCurrency(c.fees, c.currency)}
                  </span>
                ))}
              </span>
            ) : formatCurrency(summary?.platform_fees, currency)}
            subtext="BookingFlows earnings"
            icon={TrendingUp}
            testId="stat-platform-revenue"
          />
          <StatCard
            label="Total Bookings"
            value={(summary?.booking_count || 0).toLocaleString()}
            subtext={multiCurrency
              ? summary.by_currency.map((c) => `${c.bookings} ${c.currency}`).join(' · ')
              : `Avg ${formatCurrency(summary?.avg_booking_value, currency)}`}
            icon={CheckCircle}
            testId="stat-total-bookings"
          />
          <StatCard
            label="Active Companies"
            value={(summary?.active_companies_30d || 0).toLocaleString()}
            subtext="Booked in the last 30 days"
            icon={Building2}
            testId="stat-active-companies"
          />
        </div>

        {/* Revenue Trend */}
        <DataCard title="Revenue Trend" subtitle="Monthly GMV and Platform Revenue">
          <div className="h-64 sm:h-80 min-h-[256px]" data-testid="revenue-trend-chart">
            {monthlyChart.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthlyChart}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonth}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(val) => `$${val >= 1000 ? Math.round(val / 1000) + 'k' : val}`}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Bar dataKey="gmv" name="GMV" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={60} />
                  <Bar dataKey="fees" name="Platform Fee" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={60} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No data for this period
              </div>
            )}
          </div>
        </DataCard>

        {/* Revenue Split + Org Stats | Recent Signups */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DataCard title="Revenue Split & Organization Stats">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="h-[160px]">
                  {pieData.length > 0 && pieData[0].value > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value, currency)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                      No data
                    </div>
                  )}
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-500">Operators</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                    <span className="text-xs text-gray-500">Platform</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3" data-testid="org-stats-summary">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Total Companies</span>
                  <span className="font-medium text-gray-900">{stats.totalOrgs}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Active</span>
                  <span className="font-medium text-gray-900">{stats.activeOrgs}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="font-medium text-gray-900">{stats.pendingOrgs}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-600">Suspended</span>
                  <span className="font-medium text-gray-900">{stats.suspendedOrgs}</span>
                </div>
              </div>
            </div>
          </DataCard>

          <DataCard title="Recent Signups">
            <div className="space-y-3" data-testid="recent-signups-list">
              {stats.recentSignups.length > 0 ? (
                stats.recentSignups.map((org) => (
                  <div key={org.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm truncate">{org.name}</p>
                      <p className="text-xs text-gray-400 truncate">{org.ownerEmail}</p>
                    </div>
                    <StatusBadge status={org.status} />
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">No recent signups</p>
              )}
            </div>
          </DataCard>
        </div>

        {/* Company Leaderboard */}
        <DataCard title="Company Leaderboard" subtitle="Top organizations by GMV for the selected period">
          <DataTable
            columns={[
              { key: 'rank', label: '#', render: (org) => <span className="text-gray-500 font-medium">{org.rank}</span> },
              { key: 'name', label: 'Organization', render: (org) => (
                <div>
                  <p className="font-medium text-gray-900">{org.name}</p>
                  <p className="text-xs text-gray-400">{org.slug}</p>
                </div>
              ) },
              { key: 'gmv', label: 'GMV', align: 'right', render: (org) => <span className="font-medium text-gray-900">{formatCurrency(org.gmv, org.currency)}</span> },
              { key: 'fees', label: 'Platform Fee', align: 'right', render: (org) => <span className="text-gray-600">{formatCurrency(org.fees, org.currency)}</span> },
              { key: 'bookings', label: 'Bookings', align: 'right', render: (org) => <span className="text-gray-600">{org.bookings}</span> },
              { key: 'avg', label: 'Avg Booking', align: 'right', render: (org) => <span className="text-gray-600">{formatCurrency(org.avg_booking_value, org.currency)}</span> },
            ]}
            rows={topOrgs.slice(0, 10).map((org, index) => ({ ...org, rank: index + 1 }))}
            rowKey="slug"
            minWidth={560}
            emptyMessage="No revenue data for this period"
            testId="top-orgs-table"
          />
        </DataCard>
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminDashboard;
